/**
 * Size Observer Utility
 * 
 * Monitors checkout element dimensions and sends updates to parent window via postMessage.
 * Uses ResizeObserver for efficient, event-driven size tracking without polling.
 * 
 * Features:
 * - Tracks both width and height changes
 * - Debounced measurements to avoid layout thrashing
 * - Caches last sent dimensions to prevent redundant messages
 * - Automatic cleanup of observers
 * - Secure postMessage with origin validation
 */

import MessageType from '@/shared/enums/MessageType'
import Message from '@/shared/mixins/post_message/Message'

export interface SizeObserverConfig {
  targetOrigin: string
  targetWindow: Window
  debounceMs?: number
}

export interface Size {
  width: number
  height: number
}

export class SizeObserver {
  private resizeObserver: any | null = null
  private mutationObserver: any | null = null
  private lastSize: Size = { width: 0, height: 0 }
  private debounceTimer: number | null = null
  private readonly config: Required<SizeObserverConfig>

  constructor(config: SizeObserverConfig) {
    this.config = {
      debounceMs: 50,
      ...config,
    }
  }

  /**
   * Start observing element size changes
   */
  start(): void {
    // Delay initial measurement to ensure DOM is fully rendered
    window.requestAnimationFrame(() => {
      this.measureAndSend()
    })

    // Set up ResizeObserver on the checkout element
    const checkoutElement = this.getCheckoutElement()
    if (!checkoutElement) {
      console.warn('[SizeObserver] .epag-checkout element not found')
      return
    }

    this.setupResizeObserver(checkoutElement)
    this.setupMutationObserver(checkoutElement)
  }

  /**
   * Stop observing and clean up resources
   */
  stop(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    this.lastSize = { width: 0, height: 0 }
  }

  /**
   * Force an immediate size measurement and send
   * Useful for triggering updates after known DOM changes
   */
  forceUpdate(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    this.measureAndSend()
  }

  /**
   * Get the checkout element
   */
  private getCheckoutElement(): HTMLElement | null {
    return document.querySelector('.epag-checkout')
  }

  /**
   * Set up ResizeObserver
   */
  private setupResizeObserver(element: HTMLElement): void {
    const ResizeObserverConstructor = (window as any).ResizeObserver
    if (!ResizeObserverConstructor) {
      console.warn('[SizeObserver] ResizeObserver not available')
      return
    }

    this.resizeObserver = new ResizeObserverConstructor(() => {
      this.debouncedMeasureAndSend()
    })

    this.resizeObserver.observe(element)
  }

  /**
   * Set up MutationObserver to catch DOM changes
   */
  private setupMutationObserver(element: HTMLElement): void {
    const MutationObserverConstructor = (window as any).MutationObserver
    if (!MutationObserverConstructor) {
      return
    }

    this.mutationObserver = new MutationObserverConstructor(() => {
      this.debouncedMeasureAndSend()
    })

    this.mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })
  }

  /**
   * Debounced version of measureAndSend to avoid layout thrashing
   */
  private debouncedMeasureAndSend(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = window.setTimeout(() => {
      this.measureAndSend()
      this.debounceTimer = null
    }, this.config.debounceMs)
  }

  /**
   * Measure current element size and send if changed
   */
  private measureAndSend(): void {
    const currentSize = this.measureSize()

    // Only send if size has changed
    if (this.hasSizeChanged(currentSize)) {
      this.sendSizeMessage(currentSize)
      this.lastSize = currentSize
    }
  }

  /**
   * Measure the checkout element size
   */
  private measureSize(): Size {
    const element = this.getCheckoutElement()

    if (element) {
      return {
        width: element.offsetWidth,
        height: element.offsetHeight,
      }
    }

    // Fallback to document dimensions
    return {
      width: document.documentElement.clientWidth,
      height: Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      ),
    }
  }

  /**
   * Check if size has changed
   */
  private hasSizeChanged(newSize: Size): boolean {
    return (
      newSize.width !== this.lastSize.width ||
      newSize.height !== this.lastSize.height
    )
  }

  /**
   * Send size update message to parent window
   */
  private sendSizeMessage(size: Size): void {
    // Validate targetWindow and targetOrigin
    if (!this.config.targetWindow || !this.config.targetOrigin) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[SizeObserver] Missing targetWindow or targetOrigin, skipping message')
      }
      return
    }

    const message = new Message(MessageType.sizeChange, size)

    // Debug logging (remove in production)
    if (process.env.NODE_ENV !== 'production') {
      const element = this.getCheckoutElement()
      const source = element ? '.epag-checkout element' : 'document'
      console.log(
        `[SizeObserver] Sending size: ${size.width}×${size.height}px (from ${source})`
      )
    }

    this.config.targetWindow.postMessage(message, this.config.targetOrigin)
  }
}

/**
 * Create and start a size observer
 * Returns cleanup function
 */
export function createSizeObserver(config: SizeObserverConfig): () => void {
  const observer = new SizeObserver(config)
  observer.start()

  return () => observer.stop()
}
